import React, {useEffect, useState} from "react";
import {Route, Routes, useNavigate, useParams} from "react-router-dom";
import {Helmet} from "react-helmet";
import {useIntl} from "react-intl";
import {Spinner} from "../../../shared";
import {getViewMessage, getYearOfDate} from "../../../../utils/utils";
import TmdbImage from "../../../shared/tmdb-image/tmdb-image";
import TmdbScore from "../../tmdbScore/tmdbScore";
import TmdbCollectionSelector from "../../movies/tmdbCollectionSelector/tmdbCollectionSelector";
import TmdbViewLater from "../../movies/tmdbViewLater/TmdbViewLater";
import TmdbLikeItem from "../../movies/tmdbLikeItem/tmdb-like-item";
import TmdbViewToggle from "../../movies/tmdbViewToggle/tmdb-view-toggle";
import TmdbNotepad from "../../movies/tmdbNotepad/tmdbNotepad";
import TmdbItemSuggestions from "../../movies/tmdb-item-suggestions/tmdb-item-suggestions";
import TmdbItemReviews from "../../movies/tmdb-item-reviews/tmdb-item-reviews";
import useMovieDetailsData from "../../hooks/useMovieDetailsData";
import SquadflixTvPlayerView from "./SquadflixTvPlayerView";

type Props = {
    squadflixTvHook?: any;
};

const SquadflixTvMovieDetails: React.FC<Props> = ({squadflixTvHook}) => {
    const {id} = useParams();
    const navigate = useNavigate();
    const intl = useIntl();
    const [activeTab, setActiveTab] = useState<"suggestions" | "reviews" | "details">("suggestions");

    const {
        movie,
        logoImage,
        backgroundImage,
        from
    } = useMovieDetailsData(id!, squadflixTvHook);

    useEffect(() => {
        setActiveTab("suggestions");
    }, [id]);

    if (!movie) {
        return (
            <div className="tv-loading-center">
                <Spinner />
            </div>
        );
    }

    const backdropPath = backgroundImage?.file_path || movie.backdrop_path || movie.poster_path;

    return (
        <div className="tv-details-screen">
            <Helmet>
                <meta charSet="utf-8" />
                <title>{`${movie.title} | SquadFlix TV`}</title>
            </Helmet>

            {/* Full-bleed 16:9 Cinema Backdrop */}
            <div className="tv-details-screen__backdrop">
                {backdropPath && (
                    <TmdbImage
                        src={backdropPath}
                        size="original"
                        className="tv-details-screen__backdrop-img"
                    />
                )}
                <div className="tv-details-screen__vignette" />
            </div>

            {/* Foreground Content */}
            <div className="tv-details-screen__body">
                {/* Top Back Bar */}
                <div className="tv-details-screen__topbar" data-tv-row="tv-movie-topbar">
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className="tv-btn tv-btn--glass"
                        onClick={() => navigate(from?.pathName || "/squadflix")}>
                        <i className="fa-solid fa-arrow-left pe-2"></i>
                        {from?.customKey || "Volver al catálogo"}
                    </button>
                </div>

                {/* Main Hero Metadata */}
                <div className="tv-details-screen__hero">
                    {logoImage ? (
                        <TmdbImage
                            src={logoImage.file_path}
                            className="tv-details-screen__logo"
                        />
                    ) : (
                        <h1 className="tv-details-screen__title">{movie.title}</h1>
                    )}

                    <div className="tv-details-screen__meta-row">
                        <span className="tv-pill tv-pill--accent">Película</span>
                        {movie.vote_average != null && (
                            <span className="tv-pill">
                                <i className="fa-solid fa-star text-warning pe-1"></i>
                                {movie.vote_average.toFixed(1)}
                            </span>
                        )}
                        {movie.release_date && (
                            <span className="tv-pill">{getYearOfDate(movie.release_date)}</span>
                        )}
                        {movie.runtime && (
                            <span className="tv-pill">{movie.runtime} min</span>
                        )}
                        {movie.view && (
                            <span className="tv-pill tv-pill--success">
                                <i className="fa-solid fa-eye pe-1"></i>Vista
                            </span>
                        )}
                        {movie.genres?.map((g: any) => (
                            <span key={g.id} className="tv-pill">
                                {g.name}
                            </span>
                        ))}
                    </div>

                    {movie.overview && (
                        <p className="tv-details-screen__overview">{movie.overview}</p>
                    )}

                    {/* TV Action Buttons */}
                    <div className="tv-details-screen__actions" data-tv-row="tv-movie-actions">
                        <button
                            type="button"
                            data-tv-focusable="true"
                            data-tv-autofocus="true"
                            className="tv-btn tv-btn--primary tv-btn--lg"
                            onClick={() => navigate("play")}>
                            <i className="fa-solid fa-play pe-2"></i>Reproducir Película
                        </button>

                        <TmdbCollectionSelector item={movie} isMovie={true} />
                        <TmdbViewLater tmdbItem={movie} />
                        <TmdbLikeItem tmdbItem={movie} />
                        <TmdbViewToggle item={movie} />

                        <button
                            type="button"
                            data-tv-focusable="true"
                            className="tv-btn tv-btn--glass"
                            onClick={() => navigate("#notes")}>
                            <i className="fa-solid fa-note-sticky pe-2"></i>Mis Notas
                        </button>
                    </div>
                </div>

                {/* Bottom Tabs (Suggestions, Reviews, Details) */}
                <div className="tv-details-screen__tabs-section">
                    <div className="tv-details-screen__tabs" data-tv-row="tv-movie-tabs">
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-tab-btn ${activeTab === "suggestions" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("suggestions")}>
                            <i className="fa-solid fa-wand-magic-sparkles pe-2"></i>Sugerencias Similares
                        </button>
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-tab-btn ${activeTab === "reviews" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("reviews")}>
                            <i className="fa-solid fa-comments pe-2"></i>Reseñas
                        </button>
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-tab-btn ${activeTab === "details" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("details")}>
                            <i className="fa-solid fa-circle-info pe-2"></i>Ficha Técnica
                        </button>
                    </div>

                    <div
                        className="tv-details-screen__tab-panel"
                        data-tv-row="tv-movie-tab-panel"
                        data-tv-grid="true">
                        {activeTab === "suggestions" && (
                            <TmdbItemSuggestions item={movie} isMovie={true} />
                        )}
                        {activeTab === "reviews" && (
                            <TmdbItemReviews item={movie} isMovie={true} />
                        )}
                        {activeTab === "details" && (
                            <div className="tv-details-screen__info-grid">
                                <div>
                                    <h3>{movie.title}</h3>
                                    <p className="text-muted">{movie.overview}</p>
                                    {movie.view_date && (
                                        <p className="mt-3">
                                            <i className="fa-regular fa-eye pe-2"></i>
                                            {getViewMessage(intl, movie.view_date)}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <TmdbScore score={movie.vote_average} info={undefined} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Routes for /play and hidden Notepad handler */}
            <Routes>
                <Route
                    path="play"
                    element={
                        <div className="tv-fullscreen-overlay">
                            <SquadflixTvPlayerView
                                view={true}
                                imagePath={movie.backdrop_path}
                                file={movie.shared_files?.[0]}
                                overview={movie.overview}
                                item={movie}
                                playing={true}
                                type="movie"
                                squadFlixTvHook={squadflixTvHook}
                                tmdbId={movie.id}
                                onBack={() => navigate(`/squadflix/movies/${movie.id}`)}
                            />
                        </div>
                    }
                />
                <Route
                    path="*"
                    element={
                        <div style={{display: "none"}}>
                            <TmdbNotepad tmdbItem={movie} forceDarkMode="dark" customUrl={undefined} />
                        </div>
                    }
                />
            </Routes>
        </div>
    );
};

export default SquadflixTvMovieDetails;
